'use client';

import { PiCalendarBlank, PiMapPin } from 'react-icons/pi';
import EventBannerImage from '../atoms/EventBannerImage/EventBannerImage';
import usePrefetchOnIntent from '../../hooks/usePrefetchOnIntent';
import type { FeaturedBusinessGroup } from '../../utils/buildFeaturedSlides';

const FALLBACK_BANNER = '/favicon.png';
const CARD_IMAGE_SIZES = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';

interface FeaturedBusinessCardProps {
  group: FeaturedBusinessGroup;
  onSelect: (href: string) => void;
}

// One card standing in for all the events of a featured business (a festival
// with several nights). Same look as the public LandingEventCard, but it shows
// the date range and links to the business page instead of a single event.
const FeaturedBusinessCard = ({ group, onSelect }: FeaturedBusinessCardProps) => {
  const prefetchHandlers = usePrefetchOnIntent(group.href);
  return (
    <a
      href={group.href}
      onClick={(e) => {
        e.preventDefault();
        onSelect(group.href);
      }}
      {...prefetchHandlers}
      className="group flex flex-col w-full h-full p-2 pb-6 rounded-3xl border border-border-secondary bg-surface-base landing-event-card-shadow gap-3 cursor-pointer no-underline text-inherit transition-transform duration-200 hover:-translate-y-1"
    >
      <div className="relative w-full overflow-hidden rounded-2xl aspect-[1920/700] bg-surface-secondary">
        {group.isSoldOut && (
          <span className="bg-warning-300 text-text-primary absolute top-2 left-2 z-10 px-2.5 py-1 rounded-full text-xs-semibold shadow-sm">
            Entradas agotadas
          </span>
        )}
        <EventBannerImage
          src={group.bannerImage || FALLBACK_BANNER}
          alt={group.title}
          sizes={CARD_IMAGE_SIZES}
          loading="lazy"
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      <div className="flex flex-col items-center text-center w-full flex-1 gap-2.5 px-4 pt-3">
        <h2 className="text-xl-bold text-text-primary leading-tight text-pretty m-0">
          {group.title}
        </h2>
        {group.subtitle && (
          <p className="text-sm-regular text-text-tertiary m-0">{group.subtitle}</p>
        )}
        <p className="flex items-center gap-1.5 text-sm-medium text-text-secondary m-0">
          <PiCalendarBlank className="text-primary-500 text-base" />
          {group.dateLabel}
        </p>
        {group.place && (
          <p className="flex items-center gap-1.5 text-sm-medium text-text-secondary break-words whitespace-normal m-0">
            <PiMapPin className="text-primary-500 text-base" />
            {group.place}
          </p>
        )}
        <div className="flex h-full items-end mt-2">
          <span className="inline-flex items-center justify-center px-7 py-3 rounded-full border-[1.5px] border-primary-500 bg-surface-base text-text-primary text-md-bold transition-colors duration-150 group-hover:bg-primary-500 group-hover:text-white">
            {group.ctaLabel}
          </span>
        </div>
      </div>
    </a>
  );
};

export default FeaturedBusinessCard;
